import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { X, ZoomIn, ZoomOut, Move } from "lucide-react";

async function cropToBlob(imageSrc, area) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = imageSrc;
  });
  const out = Math.min(1400, Math.round(area.width));
  const canvas = document.createElement("canvas");
  canvas.width = out;
  canvas.height = Math.round(out * (area.height / area.width));
  canvas.getContext("2d").drawImage(img, area.x, area.y, area.width, area.height, 0, 0, canvas.width, canvas.height);
  return new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.92));
}

export function ImageCropModal({ file, aspect = 1, title = "Position your photo", onDone, onCancel }) {
  const [src] = useState(() => URL.createObjectURL(file));
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_, pixels) => setArea(pixels), []);

  const confirm = async () => {
    if (!area) return;
    setBusy(true);
    try {
      const blob = await cropToBlob(src, area);
      URL.revokeObjectURL(src);
      onDone(new File([blob], (file.name || "photo").replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" }));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-ink/90 backdrop-blur-sm flex items-center justify-center p-5" data-testid="crop-modal">
      <div className="w-full max-w-lg bg-surface border border-line p-5 md:p-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-cream">{title}</h3>
            <p className="font-mono text-[10px] uppercase tracking-wide text-ash mt-1 flex items-center gap-1.5"><Move size={11} /> Drag to position · pinch or slide to zoom</p>
          </div>
          <button onClick={() => { URL.revokeObjectURL(src); onCancel(); }} data-testid="crop-cancel-x" className="text-ash hover:text-gold p-1" aria-label="Cancel"><X size={20} /></button>
        </div>
        <div className="relative w-full aspect-square bg-ink border border-line">
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            showGrid={false}
          />
        </div>
        <div className="flex items-center gap-3 mt-4">
          <ZoomOut size={16} className="text-ash shrink-0" />
          <input
            type="range" min="1" max="4" step="0.05" value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            data-testid="crop-zoom-slider"
            className="flex-1 accent-[#C14D28] cursor-pointer"
          />
          <ZoomIn size={16} className="text-ash shrink-0" />
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={() => { URL.revokeObjectURL(src); onCancel(); }} data-testid="crop-cancel-btn" className="flex-1 border border-line text-cream font-mono text-xs uppercase tracking-widest py-3 hover:border-gold transition-colors">Cancel</button>
          <button onClick={confirm} disabled={busy} data-testid="crop-save-btn" className="flex-1 bg-gold text-ink font-bold uppercase tracking-widest text-xs py-3 hover:bg-gold-hi transition-colors disabled:opacity-60">
            {busy ? "Saving…" : "Use this framing"}
          </button>
        </div>
      </div>
    </div>
  );
}
